import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ChevronRight as CollapseIcon,
} from '@mui/icons-material';
import { FormSection } from './FormFieldEditor';

interface FormSectionEditorProps {
  section: FormSection;
  onUpdate: (section: FormSection) => void;
  onDelete: () => void;
  isDragging?: boolean;
}

const FormSectionEditor: React.FC<FormSectionEditorProps> = ({
  section,
  onUpdate,
  onDelete,
  isDragging = false,
}) => {
  const [localSection, setLocalSection] = useState<FormSection>(section);

  // Sync with parent updates
  useEffect(() => {
    setLocalSection(section);
  }, [section]);

  const handleSectionUpdate = (updates: Partial<FormSection>) => {
    const updatedSection = { ...localSection, ...updates };
    setLocalSection(updatedSection);
    onUpdate(updatedSection);
  };

  return (
    <Card 
      sx={{ 
        mb: 2,
        opacity: isDragging ? 0.5 : 1,
        border: isDragging ? '2px dashed #ccc' : 'none',
        borderLeft: '4px solid #2196F3',
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1
          }}>
          
          <Box sx={{
            flex: 1
          }}>
            <Grid container spacing={2}>
              {/* Section title. Order is set by dragging in the Sortering tab. */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sektionsnamn"
                  value={localSection.title}
                  onChange={(e) => handleSectionUpdate({ title: e.target.value })}
                  placeholder="t.ex. Gårdsinformation"
                  required
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Beskrivning (valfritt)"
                  value={localSection.description || ''}
                  onChange={(e) => handleSectionUpdate({ description: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="Förklaring som visas för användaren"
                />
              </Grid>

              {/* Default Expanded Toggle */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSection.defaultExpanded}
                      onChange={(e) => handleSectionUpdate({ defaultExpanded: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                      }}>
                      {localSection.defaultExpanded ? <ExpandIcon /> : <CollapseIcon />}
                      <Typography variant="body2">
                        Visa som {localSection.defaultExpanded ? 'öppen' : 'minimerad'} som standard
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

            </Grid>
          </Box>

          <IconButton
            color="error"
            onClick={onDelete}
            size="small"
            sx={{ mt: 1 }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FormSectionEditor;